import { emptySplitApi, RootState } from 'shared/store/store'

import { getActiveClient } from 'shared/store/clients/selectors'

import { GET_PUBLICATIONS } from 'shared/constants/storeTags'
import {
  IPublication,
  IPublicationCategoryModel,
  IPublicationsListModelResult,
} from 'shared/models/Publications'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'

const publishingService = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    fetchPublications: build.query<IPublicationsListModelResult, void>({
      providesTags: [GET_PUBLICATIONS],
      queryFn: async (_args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `publications/clients/${client.id}/publications/query`,
          method: 'POST',
          headers: {
            'x-api-version': '2.0',
          },
          data: {
            webApp: 'BoardPortal',
            take: 100,
            skip: 0,
            sortOrder: 'Descending',
          },
        })
        if (response.error) return { error: response.error }
        return {
          data: response.data as IPublicationsListModelResult,
        }
      },
    }),

    fetchPublicationById: build.query<IPublication, { publicationId: string }>({
      queryFn: async (args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `publications/clients/${client.id}/publications/${args.publicationId}`,
          method: 'GET',
          headers: {
            'x-api-version': '2.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av nyheten.',
          })
          return { error }
        }
        return {
          data: response.data as IPublication,
        }
      },
    }),
    fetchServiceNews: build.query<IPublication, void>({
      queryFn: async (_args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `publications/clients/${client.id}/publications/boardportal/servicemessage`,
          method: 'GET',
          headers: {
            'x-api-version': '2.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av driftsmeldinger.',
          })
          return { error }
        }
        return {
          data: response.data as IPublication,
        }
      },
    }),
    fetchPublicationCategories: build.query<IPublicationCategoryModel[], void>({
      queryFn: async (_args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `publications/PublicationsCategory`,
          method: 'GET',
          headers: {
            'x-api-version': '2.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av kategorier.',
          })
          return { error }
        }
        return {
          data: response.data as IPublicationCategoryModel[],
        }
      },
    }),
    setPublicationAsReadForUser: build.mutation<
      void,
      { publicationId: string }
    >({
      queryFn: async (args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `publications/clients/${client.id}/publications/${args.publicationId}/setread`,
          method: 'PUT',
          headers: {
            'x-api-version': '2.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError:
              'En feil har oppstått i setting av lest for brukeren.',
          })
          return { error }
        }
        return {
          data: undefined,
        }
      },
    }),
  }),
  overrideExisting: false,
})

export default publishingService
export const {
  useFetchPublicationsQuery,
  useFetchServiceNewsQuery,
  useFetchPublicationByIdQuery,
  useFetchPublicationCategoriesQuery,
  useSetPublicationAsReadForUserMutation,
} = publishingService
